import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { useQuery, useMutation, skipToken } from '@apollo/client/react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { PageTitle } from 'common/components/PageTitle';
import {
    DeleteSkillsDocument,
    SkillDocument,
    UpdateSkillDocument,
    IFileFragment,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { SkillForm } from 'skill/components/SkillForm';
import { TSkillFormValues } from 'skill/types';
import { getImageId } from 'common/utils/image';
import { Loader } from 'common/components/Loader';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';
import { ERROR_CODES } from 'common/constants/errorCodes';
import { closeGlobalDrawerRoute } from 'common/utils/globalDrawer';

interface IProps extends IPageDrawerProps {
    onClose?(): void;
    skillId?: string;
}

export const SkillUpdateDrawer = ({
    open,
    skillId,
    onClose,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const { canUpdate = false } = useAppPermissions('skill', 'management');

    const { id: skillIdParam } = useParams();
    const id = skillId || skillIdParam;

    const { data: skillData, loading: skillLoading } = useQuery(
        SkillDocument,
        open && id && canUpdate ? { variables: { id } } : skipToken
    );
    const [updateSkill, { loading: updateSkillLoading }] =
        useMutation(UpdateSkillDocument);
    const [deleteSkills, { loading: deleteSkillsLoading }] =
        useMutation(DeleteSkillsDocument);

    if (!canUpdate) return <FourOFourPage />;

    const {
        name,
        shortName,
        description,
        groupRequirements,
        skillCertificates,
        thumbnail,
    } = skillData?.skill || {};

    const handleClose = () => {
        closeGlobalDrawerRoute(navigate, location, true);
    };

    const handleSubmit = async (values: TSkillFormValues) => {
        const {
            groups,
            certificates,
            thumbnail: newThumbnail,
            ...otherValues
        } = values;

        const skillGroups = groups?.map((req) => ({
            groupId: req.group.id,
            minAbsolute: req.minAbsolute
                ? parseInt(String(req.minAbsolute), 10)
                : null,
            minPercent: req.minPercent
                ? parseInt(String(req.minPercent), 10)
                : null,
        }));
        const certificateInputs = certificates?.map((certificate) => ({
            id: certificate.id,
            isMandatory: !!certificate.isMandatory,
        }));

        try {
            await updateSkill({
                variables: {
                    id: id!,
                    skill: {
                        groups: skillGroups,
                        certificates: certificateInputs,
                        thumbnailId: getImageId(
                            thumbnail as IFileFragment | undefined,
                            newThumbnail
                        ),
                        ...otherValues,
                    },
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'skills' });
                },
            });
        } catch (error) {
            if (CombinedGraphQLErrors.is(error)) {
                for (const err of error.errors) {
                    const { code, ids } = err?.extensions || {};

                    if (!code) {
                        showApolloError(error);

                        return;
                    }

                    if (code === ERROR_CODES.GROUPS_NOT_ADDED_TO_SKILL) {
                        const groupIds = ids as string[] | undefined;
                        const groupNames = groupIds
                            ?.map((id: string) => {
                                const req = groups?.find(
                                    (req) => req.group.id === id
                                );

                                return req?.group.name;
                            })
                            .join(', ');

                        displaySnackbar(
                            translate('skillForm.groupsNotAdded', {
                                groupNames,
                                count: groupIds?.length,
                            }),
                            {
                                variant: 'error',
                            }
                        );
                    }

                    if (code === ERROR_CODES.CERTIFICATES_NOT_ADDED_TO_SKILL) {
                        const certIds = ids as string[] | undefined;
                        const certificateNames = certIds
                            ?.map((id: string) => {
                                const certificate = certificates?.find(
                                    (certificate) => certificate.id === id
                                );

                                return certificate?.name;
                            })
                            .join(', ');

                        displaySnackbar(
                            translate('skillForm.certificatesNotAdded', {
                                certificateNames,
                                count: certIds?.length,
                            }),
                            {
                                variant: 'error',
                            }
                        );
                    }
                }
            }

            return;
        }

        displaySnackbar(
            translate('skillsManagementPage.actionSuccess.update'),
            {
                variant: 'success',
            }
        );

        handleClose();

        return;
    };

    const handleDelete = async () => {
        try {
            await deleteSkills({
                variables: { ids: [id!] },
                update: (cache) => {
                    cache.evict({ fieldName: 'skills' });
                },
            });
        } catch (error) {
            showApolloError(error);
        }

        setShowDeleteDialog(false);

        handleClose();
    };

    const initialValues: TSkillFormValues = {
        name: name || '',
        shortName: shortName || '',
        description: description || '',
        groups: (groupRequirements || [])
            .filter((req) => !!req?.group)
            .map((req) => ({
                group: req!.group!,
                minAbsolute: req!.minAbsolute ?? null,
                minPercent: req!.minPercent ?? null,
            })),
        certificates: (skillCertificates || []).filter(Boolean).map((sc) => ({
            ...sc.certificate,
            isMandatory: sc.isMandatory,
        })) as TSkillFormValues['certificates'],
        thumbnail: thumbnail as TSkillFormValues['thumbnail'],
    };

    const loading = skillLoading;

    return (
        <PageDrawer open={open} onClose={handleClose} {...other}>
            <PageTitle>{`${name} - ${translate('skillForm.editSkill')}`}</PageTitle>

            <Box sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 0, sm: 0 } }}>
                {loading && <Loader />}

                {!loading && (
                    <SkillForm
                        isEdit
                        disabled={updateSkillLoading}
                        initialValues={initialValues}
                        onClose={handleClose}
                        onDelete={() => setShowDeleteDialog(true)}
                        onSubmit={handleSubmit}
                    />
                )}
            </Box>

            <ConfirmDialog
                confirmText={translate('delete')}
                loading={deleteSkillsLoading}
                open={showDeleteDialog}
                title={translate('deleteSkillMessage.title', { count: 1 })}
                onCancel={() => {
                    setShowDeleteDialog(false);
                }}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
            >
                {translate('deleteSkillMessage.text', { count: 1 })}
            </ConfirmDialog>
        </PageDrawer>
    );
};
